import { message } from 'antd';
import type { Editor } from '@tiptap/react';
import { replaceImageAt, resolvePos, setImageUploading, type ReplaceImageInput } from './image-replace';

type GetPos = () => number | undefined;
type UploadImage = (f: File) => Promise<{ url: string }>;

const GONE = '原本那張圖已不在內文中，沒有替換';

/** 在游標處插入一張媒體庫既有照片（預設置中、無圖說）。 */
export function insertImage(editor: Editor, input: ReplaceImageInput): void {
  editor
    .chain()
    .focus()
    .insertContent({
      type: 'image',
      attrs: { src: input.src, alt: input.alt ?? '', align: 'center', caption: '' },
    })
    .run();
}

/**
 * 替換 getPos 指向的那張圖。位置在「挑完的當下」才解析，
 * 挑圖期間內文若有增刪，仍會換到正確的那張；那張圖已被刪除則提示、不動文件。
 */
export function replaceImage(editor: Editor, getPos: GetPos, input: ReplaceImageInput): boolean {
  const pos = resolvePos(getPos);
  const ok =
    pos != null &&
    editor
      .chain()
      .command(({ tr }) => replaceImageAt(tr, pos, input))
      .setNodeSelection(pos)
      .run();
  if (!ok) message.warning(GONE);
  return ok;
}

/** 上傳新檔取代既有那張圖：上傳中顯示轉圈，成功換 src，失敗還原並提示。 */
export async function uploadReplaceImage(
  editor: Editor,
  getPos: GetPos,
  file: File,
  uploadImage: UploadImage,
  onDone: () => void,
): Promise<void> {
  if (!file.type.startsWith('image/')) {
    message.error('只能上傳圖片檔');
    return;
  }
  const start = resolvePos(getPos);
  if (start == null) return;
  editor.commands.command(({ tr }) => setImageUploading(tr, start, true));

  try {
    const { url } = await uploadImage(file);
    if (replaceImage(editor, getPos, { src: url })) onDone();
  } catch (e) {
    const pos = resolvePos(getPos);
    if (pos != null) editor.commands.command(({ tr }) => setImageUploading(tr, pos, false));
    message.error(e instanceof Error ? e.message : '圖片上傳失敗');
  }
}
